export type ConsentCategories = {
  necessary: true;
  analytics: boolean;
  marketing: boolean;
};

const STORAGE_KEY = "cookie-consent-v1";

export function getStoredConsent(): ConsentCategories | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as ConsentCategories) : null;
  } catch {
    return null;
  }
}

export function saveConsent(consent: ConsentCategories, action: ConsentAction = "save_custom") {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
  } catch {
    /* ignore */
  }
  updateGtagConsent(consent);
  logConsentEvent(consent, action);
  window.dispatchEvent(new CustomEvent("cookie-consent-changed", { detail: consent }));
}

export type ConsentAction = "accept_all" | "reject_all" | "save_custom" | "update";

function logConsentEvent(consent: ConsentCategories, action: ConsentAction) {
  // 1) Push to GA / GTM dataLayer for analytics dashboards
  try {
    const w = window as unknown as { dataLayer?: unknown[]; gtag?: (...args: unknown[]) => void };
    w.dataLayer = w.dataLayer || [];
    w.dataLayer.push({
      event: "cookie_consent",
      consent_action: action,
      consent_analytics: consent.analytics,
      consent_marketing: consent.marketing,
      page_path: window.location.pathname,
    });
    if (typeof w.gtag === "function") {
      w.gtag("event", "cookie_consent", {
        consent_action: action,
        consent_analytics: consent.analytics,
        consent_marketing: consent.marketing,
      });
    }
  } catch {
    /* ignore */
  }

  // 2) Persist to backend for internal reporting (via edge function)
  import("@/integrations/supabase/client")
    .then(({ supabase }) =>
      supabase.functions.invoke("log-consent", {
        body: {
          action,
          analytics: consent.analytics,
          marketing: consent.marketing,
          page_path: window.location.pathname,
          referrer: document.referrer || null,
        },
      })
    )
    .catch(() => {
      /* non-blocking */
    });
}



export function updateGtagConsent(consent: ConsentCategories) {
  const gtag = (window as unknown as { gtag?: (...args: unknown[]) => void }).gtag;
  if (typeof gtag !== "function") return;
  gtag("consent", "update", {
    ad_storage: consent.marketing ? "granted" : "denied",
    ad_user_data: consent.marketing ? "granted" : "denied",
    ad_personalization: consent.marketing ? "granted" : "denied",
    analytics_storage: consent.analytics ? "granted" : "denied",
  });
}

export const ACCEPT_ALL: ConsentCategories = { necessary: true, analytics: true, marketing: true };
export const REJECT_ALL: ConsentCategories = { necessary: true, analytics: false, marketing: false };
